




















import { computed, defineComponent, ref } from "@vue/composition-api";
import { TalentAgent } from "@scrile/api-provider/dist/api/TalentAgentsProvider";
import { EarningReportItem, EarningReportItemData } from "@scrile/api-provider/dist/api/TransactionProvider";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import { getAllPageableData } from "@/lib/requestHelpers";
import { stringToColour } from "@/lib/textHelpers";
import BasePage from "@/components/BasePage";
import EmptyContent from "@/components/EmptyContent";
import FormatMoney from "@/components/FormatMoney";
import PageTitle from "@/components/PageTitle";
import StatementsItem from "./components/StatementsItem";
import { getEmptyEarningReportTypeStats, getStatType } from "./helpers";
import { EarningReportData, StatField } from "./types";

const CREATOR_STAT_FIELDS: StatField[] = [
  {
    key: "subscriptions",
    label: $t(`Subscriptions`),
    color: "#538CD9",
    selector: (stat: EarningReportItemData) => {
      return stat.type === "EARNING" && stat.contentType === "subscription";
    },
  },
  {
    key: "posts",
    label: $t(`Posts`),
    color: "#FCB64E",
    selector: (stat: EarningReportItemData) => {
      return stat.type === "EARNING" && stat.contentType === "content_set";
    },
  },
  {
    key: "messages",
    label: $t(`Messages`),
    color: "#E27272",
    selector: (stat: EarningReportItemData) => {
      return stat.type === "EARNING" && stat.contentType === "PerSentMessage";
    },
  },
  {
    key: "attachments",
    label: $t(`Attachments`),
    color: "#845DC5",
    selector: (stat: EarningReportItemData) => {
      return stat.type === "EARNING" && stat.contentType === "message_attachment";
    },
  },
  {
    key: "tips",
    label: $t(`Tips`),
    color: "#F7A4C6",
    selector: (stat: EarningReportItemData) => {
      return stat.type === "EARNING" && stat.contentType === "tip";
    },
  },
  {
    key: "referralBonus",
    label: $t(`Referral bonus`),
    color: "#40A290",
    selector: (stat: EarningReportItemData) => {
      return stat.type === "EARNING" && stat.contentType === "referral_earnings";
    },
  },
  {
    key: "goalDonations",
    label: $t(`Goal donations`),
    color: "#FB50AE",
    selector: (stat: EarningReportItemData) => {
      return (
        stat.type === "EARNING" &&
        (stat.contentType === "fundraising_goal_tip" || stat.contentType === "fundraising_goal_reward")
      );
    },
  },
];

export default defineComponent({
  name: "PageStatements",
  components: { BasePage, PageTitle, FormatMoney, StatementsItem, EmptyContent },
  setup() {
    const { authUserStrict, isCreator, isTalentAgent } = useAuthUser();
    const reports = ref<EarningReportData[]>([]);
    const loading = ref(false);
    const talentAgents = ref<TalentAgent[]>([]);

    const fields = computed<StatField[]>(() => {
      let result: StatField[] = [];
      if (isCreator.value) {
        result = result.concat(CREATOR_STAT_FIELDS);
      } else if (isTalentAgent.value) {
        result = result.concat(
          talentAgents.value.reduce<StatField[]>((out, talentAgent) => {
            out.push({
              key: talentAgent.userId,
              label: talentAgent.user?.screenName ?? $t("User deleted"),
              color: stringToColour(talentAgent.user?.username ?? `deleted${talentAgent.userId}`),
              selector: (stat: EarningReportItemData) =>
                stat.type === "EARNING" && stat.contentType === "agent_earnings" && stat.userId === talentAgent.userId,
            });
            return out;
          }, [])
        );
      }
      result.push({
        key: "other",
        label: $t(`Other`),
        selector: () => true,
      });
      return result;
    });

    const activeItem = ref("none");
    const toggleActiveItem = (item: EarningReportData) => {
      activeItem.value = item.timeFrom === activeItem.value ? "none" : item.timeFrom;
    };
    const isActiveItem = (item: EarningReportData) => activeItem.value === item.timeFrom;

    const getReports = async () => {
      try {
        loading.value = true;
        let earningReports: EarningReportItem[] = [];
        const requests: Promise<any>[] = [
          providers.TransactionProvider.getEarningReports({ data: { userId: authUserStrict.value.id } }).then(
            r => (earningReports = r)
          ),
        ];
        if (isTalentAgent.value) {
          requests.push(
            getAllPageableData(page =>
              providers.TalentAgentsProvider.search({ data: { agentId: authUserStrict.value.id, page, size: 1000 } })
            ).then(r => (talentAgents.value = r.result))
          );
        }
        await Promise.all(requests);

        const allTimeReport: EarningReportData = {
          timeTo: "",
          timeFrom: "",
          details: [],
          typeStats: getEmptyEarningReportTypeStats(fields.value),
          chartData: [],
        };
        reports.value = earningReports.map(item => {
          const typeStats = getEmptyEarningReportTypeStats(fields.value);

          item.details.forEach(stat => {
            const key = getStatType(stat, fields.value);
            typeStats[key].amount += stat.amount;
            typeStats[key].agentFee += stat.agentFee;
            typeStats[key].fee += stat.fee;
            typeStats[key].totalAmount += stat.totalAmount;

            allTimeReport.typeStats[key].amount += stat.amount;
            allTimeReport.typeStats[key].agentFee += stat.agentFee;
            allTimeReport.typeStats[key].fee += stat.fee;
            allTimeReport.typeStats[key].totalAmount += stat.totalAmount;
          });
          return {
            ...item,
            typeStats: typeStats,
            chartData: [],
          };
        });
        if (reports.value.length > 0) {
          allTimeReport.chartData = reports.value;
          toggleActiveItem(allTimeReport);
          reports.value = [allTimeReport].concat(reports.value);
        }
      } finally {
        loading.value = false;
      }
    };

    getReports();

    return { loading, reports, toggleActiveItem, isActiveItem, fields };
  },
});
